import {
  RazorpayOrder,
  RazorpayPaymentData,
} from "../../../api/webshop/rzp/RazorpayOrdersApi";
import { quarterKey } from "../revenue/revenueData";

export type SimplyWebRevenueOrder = {
  orderId: string;
  orderDate: string;
  currency: string;
  amountMinor: number;
  quarter: string;
};

function payment(order: RazorpayOrder): RazorpayPaymentData | undefined {
  return order.OrderDetails?.rzp_payment_process_response
    ?? order.OrderDetails?.rzp_create_order_response;
}

function isSuccessful(order: RazorpayOrder, paymentData?: RazorpayPaymentData) {
  const statuses = [order.Status, paymentData?.status]
    .map(status => status?.toLowerCase());
  return statuses.some(status => (
    status === "success" || status === "paid" || status === "captured"
  )) || paymentData?.captured === true;
}

function orderDate(order: RazorpayOrder, paymentData?: RazorpayPaymentData) {
  const value = order.OrderDt
    ?? order.OrderDetails?.OrderDt
    ?? paymentData?.created_at;
  if (typeof value === "number") return new Date(value * 1000).toISOString();
  if (typeof value !== "string" || Number.isNaN(new Date(value).getTime())) {
    return undefined;
  }
  return value;
}

export function buildSimplyWebRevenueOrders(
  orders: RazorpayOrder[],
): SimplyWebRevenueOrder[] {
  return orders.flatMap(order => {
    const paymentData = payment(order);
    const date = orderDate(order, paymentData);
    const amount = paymentData?.amount_paid ?? paymentData?.amount;
    const currency = paymentData?.currency ?? order.OrderDetails?.currency;

    if (
      !isSuccessful(order, paymentData)
      || !date
      || typeof amount !== "number"
      || !Number.isFinite(amount)
      || typeof currency !== "string"
      || currency.trim() === ""
    ) {
      return [];
    }

    return [{
      orderId: order.OrderId,
      orderDate: date,
      currency: currency.toUpperCase(),
      amountMinor: Math.round(amount),
      quarter: quarterKey(date),
    }];
  }).toSorted((left, right) => (
    right.orderDate.localeCompare(left.orderDate)
    || left.orderId.localeCompare(right.orderId)
  ));
}

export function summarizeSimplyWebRevenue(
  orders: SimplyWebRevenueOrder[],
): Record<string, number> {
  return orders.reduce<Record<string, number>>((totals, order) => {
    totals[order.currency] = (totals[order.currency] ?? 0) + order.amountMinor;
    return totals;
  }, {});
}
